import React, { useState, useRef, useLayoutEffect } from 'react';

/**
 * Componente para exibir descrições que podem ser expandidas ou retraídas.
 * Integra o "Ver mais" diretamente ao final do texto truncado.
 */
export default function ExpandableDescription({ 
  text, 
  className = "", 
  buttonClassName = "text-sky-600 dark:text-sky-400" 
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayedText, setDisplayedText] = useState(text);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!text) return;

    const checkAndTruncate = () => {
      const container = containerRef.current;
      if (!container) return;

      // Se estiver expandido, mostramos o texto completo
      if (isExpanded) {
        setDisplayedText(text);
        setIsTruncated(true); // Mantemos como true para mostrar o "Ver menos"
        return;
      }

      // Medição para truncamento manual
      const style = window.getComputedStyle(container);
      const lineHeight = parseFloat(style.lineHeight);
      const maxHeight = lineHeight * 2.1; // Limite de 2 linhas com pequena margem

      const tester = document.createElement('div');
      tester.style.width = `${container.clientWidth}px`;
      tester.style.fontSize = style.fontSize;
      tester.style.fontFamily = style.fontFamily;
      tester.style.fontWeight = style.fontWeight;
      tester.style.lineHeight = style.lineHeight;
      tester.style.letterSpacing = style.letterSpacing;
      tester.style.position = 'absolute';
      tester.style.visibility = 'hidden';
      tester.style.whiteSpace = 'normal';
      tester.style.wordBreak = 'break-word';
      document.body.appendChild(tester);

      tester.innerText = text;

      if (tester.clientHeight <= maxHeight) {
        setDisplayedText(text);
        setIsTruncated(false);
      } else {
        setIsTruncated(true);
        // Busca binária para o ponto de truncamento ideal
        let low = 0;
        let high = text.length;
        let best = 0;

        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          tester.innerText = text.slice(0, mid) + '... VER MAIS'; // Espaço para o botão
          if (tester.clientHeight <= maxHeight) {
            best = mid;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }
        setDisplayedText(text.slice(0, best).trim());
      }

      document.body.removeChild(tester);
    };

    checkAndTruncate();
    window.addEventListener('resize', checkAndTruncate);
    return () => window.removeEventListener('resize', checkAndTruncate);
  }, [text, isExpanded]);

  if (!text) return null;

  const toggleExpand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div ref={containerRef} className={`${className} leading-relaxed`}>
      <p className="inline">
        {isExpanded ? text : displayedText}
        {!isExpanded && isTruncated && (
          <span className="inline">
            ...{' '}
            <button
              type="button"
              onClick={toggleExpand}
              className={`font-black hover:underline focus:outline-none text-[10px] uppercase tracking-wider align-baseline ${buttonClassName}`}
            >
              Ver mais
            </button>
          </span>
        )}
        {isExpanded && (
          <button
            type="button"
            onClick={toggleExpand}
            className={`ml-1.5 font-black hover:underline focus:outline-none text-[10px] uppercase tracking-wider inline-block align-baseline ${buttonClassName}`}
          >
            Ver menos
          </button>
        )}
      </p>
    </div>
  );
}
